/******************************************************************
 * ##: Haversine distance between two GPS coordinates
 * Computes the great-circle distance (in meters) between two points given as
 * { latitude, longitude }. Pure function, safe for unit testing.
 *
 * TL;DR: Distance in meters between two lat/lng points.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

const EARTH_RADIUS_METERS = 6371000;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

/**
 * Great-circle distance in meters between two coordinates.
 *
 * @param {{ latitude: number, longitude: number }} from - First coordinate
 * @param {{ latitude: number, longitude: number }} to - Second coordinate
 * @returns {number} Distance in meters, or NaN if any coordinate is invalid
 */
export const haversineMeters = (from, to) => {
  if (!from || !to) return NaN;

  const lat1 = Number(from.latitude);
  const lon1 = Number(from.longitude);
  const lat2 = Number(to.latitude);
  const lon2 = Number(to.longitude);

  if ([lat1, lon1, lat2, lon2].some((value) => Number.isNaN(value))) {
    return NaN;
  }

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
};
